import React, { useEffect, useState } from 'react';
import axios from 'axios';
import FilterDrawer from './FilterDrawer';
import CancelButton from './CancelButton';
import { ColumnGroupHeading, encodeColumns, shownColumns, SystemColumnRows } from './ColumnPicker';
import { askConfirm, showMessage } from './AppDialogs';

// Default + a user's own named, persistent filters, as tabs on the main
// screen — quick-access/switching lives here. Building, editing, and
// saving a combination happens in the drawer (FilterDrawer.js), opened via
// a saved tab's own pencil (edit) or "+ New" (create) — never live-applied
// while the drawer is open, unlike the screen's own separate, always-
// visible "quick row" for ad hoc narrowing. `renderFields` lets the parent
// supply its own screen-specific controls (Category, UoM, whatever) bound
// to the drawer's local draft, without this component needing to know
// what any of them mean — that's what keeps it reusable across screens.
//
// Default is always first, never deletable, never edited — it's the fixed
// baseline every screen already had (whatever its own default filter state
// is), passed in as `defaultFilters` rather than owned here.
//
// `systemTabs` (optional) are further fixed tabs right after Default —
// [{ key, label, filters }], each applying `filters` over the defaults.
// Like Default they can't be edited, deleted, or reordered (Prospects &
// Customers' Prospects/Customers tabs). `initialSelectedKey` picks which fixed
// tab starts selected when the screen opens on one.
//
// Every other tab is drag-to-reorder. The order is personal (see
// backend/models/userTabOrder.js) — it follows the signed-in staff member,
// not the tenant as a whole, so two people on the same
// screen can each keep their own tab order.
//
// `tabColumns` (optional, from useTabColumns) lets a saved tab carry its
// own column set: selecting a tab hands it to the screen's ColumnPicker,
// and the drawer gains a Columns section ("Use my default columns", or a
// checklist of this tab's own). Stored in saved_filters.visible_columns,
// kept apart from filter_values.
// Underlined-text tab style — matches the entity detail page's Activities
// tabs (CommercialEntityDetailPage.js) so saved-view tabs read the same way
// wherever tabs appear in the app, rather than the older folder-tab look.
const TABS_ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', borderBottom: '1px solid #b3bbc7' };

const TAB_WRAP_STYLE = { display: 'flex', alignItems: 'center', gap: 2, paddingBottom: 10 };

const TAB_BUTTON_STYLE = {
  fontFamily: 'inherit',
  fontSize: 14,
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  padding: 0,
};

const TAB_RENAME_STYLE = {
  fontFamily: 'inherit',
  fontSize: 11,
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  color: '#9aa5b5',
  padding: '0 0 0 4px',
  lineHeight: 1,
};

const NEW_TAB_BUTTON_STYLE = {
  ...TAB_WRAP_STYLE,
  fontFamily: 'inherit',
  fontSize: 13,
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  color: '#5a6b85',
  borderBottom: '2px solid transparent',
};

const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const DRAWER_COLUMN_ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

function tabColors(selected) {
  return {
    fontWeight: selected ? 700 : 400,
    color: selected ? '#222' : '#666',
    borderBottom: selected ? '2px solid #000' : '2px solid transparent',
  };
}

// Applies a saved drag order (a list of saved_filters ids) to the rows
// fetched from the server: known ids come first in that order, anything
// new (created since the order was last saved, or never reordered) falls
// in afterward in whatever order the server returned it.
function applyTabOrder(rows, order) {
  if (!order || !order.length) return rows;
  const byId = new Map(rows.map((r) => [r.id, r]));
  const ordered = order.map((id) => byId.get(id)).filter(Boolean);
  const orderedIds = new Set(ordered.map((r) => r.id));
  const remaining = rows.filter((r) => !orderedIds.has(r.id));
  return [...ordered, ...remaining];
}

function SavedFilterTabs({ screenKey, defaultFilters, currentFilters, onApply, renderFields, systemTabs = [], initialSelectedKey = 'default', tabColumns }) {
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedKey, setSelectedKey] = useState(initialSelectedKey);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // The SavedFilter row being edited, or null when the drawer is building
  // a brand-new one instead.
  const [editingFilter, setEditingFilter] = useState(null);
  const [draft, setDraft] = useState(null);
  // The drawer's column set for this tab — null means "Use my default
  // columns". Held apart from `draft` so it never lands in filter_values.
  const [draftColumns, setDraftColumns] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draggedId, setDraggedId] = useState(null);

  const fetchSavedFilters = () => {
    Promise.all([
      axios.get('/api/saved-filters', { params: { screen_key: screenKey } }),
      axios.get('/api/tab-order', { params: { screen_key: screenKey } }).catch(() => ({ data: { data: { order: [] } } })),
    ])
      .then(([filtersRes, orderRes]) => {
        const rows = filtersRes.data.data || [];
        const order = orderRes.data.data?.order || [];
        setSavedFilters(applyTabOrder(rows, order));
      })
      .catch(() => setSavedFilters([]));
  };

  useEffect(() => {
    fetchSavedFilters();
    // eslint-disable-next-line
  }, [screenKey]);

  const persistTabOrder = (orderedFilters) => {
    axios.put('/api/tab-order', { screen_key: screenKey, order: orderedFilters.map((f) => f.id) }).catch(() => {});
  };

  const handleTabDragStart = (id) => {
    setDraggedId(id);
  };

  const handleTabDragOver = (e) => {
    e.preventDefault();
  };

  const handleTabDrop = (targetId) => {
    if (draggedId == null || draggedId === targetId) {
      setDraggedId(null);
      return;
    }
    setSavedFilters((prev) => {
      const fromIdx = prev.findIndex((f) => f.id === draggedId);
      const toIdx = prev.findIndex((f) => f.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      persistTabOrder(next);
      return next;
    });
    setDraggedId(null);
  };

  const selectDefault = () => {
    setSelectedKey('default');
    onApply(defaultFilters);
    if (tabColumns) tabColumns.selectTab(null);
  };

  const selectSystem = (tab) => {
    setSelectedKey(tab.key);
    onApply({ ...defaultFilters, ...tab.filters });
    if (tabColumns) tabColumns.selectTab(null);
  };

  const selectSaved = (savedFilter) => {
    setSelectedKey(savedFilter.id);
    if (tabColumns) tabColumns.selectTab(savedFilter);
    // Defaults underneath, so a tab saved before a filter key existed
    // (or one that never set it) reads as that key's default rather than
    // undefined — which the Filters badge would otherwise count as active.
    onApply({ ...defaultFilters, ...(savedFilter.filter_values || {}) });
  };

  const setDraftField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const openCreateDrawer = () => {
    setEditingFilter(null);
    setDraft({ name: '', ...currentFilters });
    setDraftColumns(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (savedFilter) => {
    setEditingFilter(savedFilter);
    setDraft({ name: savedFilter.name, ...savedFilter.filter_values });
    // The selected tab's column set may have been changed from the picker
    // since these rows were fetched — prefer that live copy.
    const liveColumns = tabColumns?.activeTab?.id === savedFilter.id
      ? tabColumns.activeTab.visible_columns
      : savedFilter.visible_columns;
    setDraftColumns(Array.isArray(liveColumns) ? shownColumns(liveColumns) : null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingFilter(null);
    setDraft(null);
    setDraftColumns(null);
  };

  const toggleUseDefaultColumns = (useDefault) => {
    setDraftColumns(useDefault ? null : tabColumns.getCurrentVisible());
  };

  const toggleDraftColumn = (key) => {
    setDraftColumns((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleDrawerSave = async () => {
    const trimmedName = (draft.name || '').trim();
    if (!trimmedName) {
      await showMessage('This tab needs a name', { title: 'Name Needed' });
      return;
    }
    const { name, ...filterValues } = draft;
    const body = { name: trimmedName, filter_values: filterValues };
    if (tabColumns) body.visible_columns = draftColumns === null ? null : encodeColumns(draftColumns, tabColumns.columns);
    setSaving(true);
    try {
      const res = editingFilter
        ? await axios.put(`/api/saved-filters/${editingFilter.id}`, body)
        : await axios.post('/api/saved-filters', { screen_key: screenKey, ...body });
      const saved = res.data.data;
      fetchSavedFilters();
      setSelectedKey(saved.id);
      onApply(filterValues);
      if (tabColumns) tabColumns.tabSaved(saved);
      closeDrawer();
    } catch (err) {
      await showMessage(err.response?.data?.message || 'Could not save this tab', { title: 'Couldn’t Save Tab' });
    }
    setSaving(false);
  };

  const handleDelete = async (savedFilter) => {
    if (!(await askConfirm(`Delete tab "${savedFilter.name}"?`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/saved-filters/${savedFilter.id}`);
      setSavedFilters((prev) => prev.filter((f) => f.id !== savedFilter.id));
      if (selectedKey === savedFilter.id) selectDefault();
    } catch (err) {
      await showMessage('Could not delete this tab', { title: 'Couldn’t Delete Tab' });
    }
  };

  const handleDrawerDelete = async () => {
    if (!editingFilter) return;
    await handleDelete(editingFilter);
    closeDrawer();
  };

  return (
    <div style={TABS_ROW_STYLE}>
      <div style={{ ...TAB_WRAP_STYLE, borderBottom: tabColors(selectedKey === 'default').borderBottom }}>
        <button type="button" onClick={selectDefault} style={{ ...TAB_BUTTON_STYLE, fontWeight: tabColors(selectedKey === 'default').fontWeight, color: tabColors(selectedKey === 'default').color }}>
          Default
        </button>
      </div>
      {systemTabs.map((tab) => (
        <div key={tab.key} style={{ ...TAB_WRAP_STYLE, borderBottom: tabColors(selectedKey === tab.key).borderBottom }}>
          <button type="button" onClick={() => selectSystem(tab)} style={{ ...TAB_BUTTON_STYLE, fontWeight: tabColors(selectedKey === tab.key).fontWeight, color: tabColors(selectedKey === tab.key).color }}>
            {tab.label}
          </button>
        </div>
      ))}
      {savedFilters.map((sf) => (
        <div
          key={sf.id}
          draggable
          onDragStart={() => handleTabDragStart(sf.id)}
          onDragOver={handleTabDragOver}
          onDrop={() => handleTabDrop(sf.id)}
          onDragEnd={() => setDraggedId(null)}
          title="Drag to reorder"
          style={{ ...TAB_WRAP_STYLE, borderBottom: tabColors(selectedKey === sf.id).borderBottom, cursor: 'grab', opacity: draggedId === sf.id ? 0.4 : 1 }}
        >
          <button type="button" onClick={() => selectSaved(sf)} style={{ ...TAB_BUTTON_STYLE, fontWeight: tabColors(selectedKey === sf.id).fontWeight, color: tabColors(selectedKey === sf.id).color }}>
            {sf.name}
          </button>
          <button type="button" onClick={() => openEditDrawer(sf)} title="Edit this tab" aria-label={`Edit ${sf.name}`} style={TAB_RENAME_STYLE}>
            ✎
          </button>
        </div>
      ))}
      <button type="button" onClick={openCreateDrawer} title="Build and save a new tab" style={NEW_TAB_BUTTON_STYLE}>
        + New
      </button>

      {draft && (
        <FilterDrawer
          open={drawerOpen}
          onOpenChange={(next) => { if (!next) closeDrawer(); }}
          title={editingFilter ? 'Edit Tab' : 'New Tab'}
          footer={(
            <>
              <div>
                {editingFilter && (
                  <button type="button" onClick={handleDrawerDelete} style={{ padding: '8px 12px', border: '1px solid #b91c1c', color: '#b91c1c', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                    Delete
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeDrawer} disabled={saving} />
                <button type="button" onClick={handleDrawerSave} disabled={saving} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, opacity: saving ? 0.6 : 1 }}>
                  Save
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={FIELD_LABEL_STYLE}>Tab name</label><br />
              <input
                autoFocus
                value={draft.name}
                onChange={(e) => setDraftField('name', e.target.value)}
                style={FIELD_INPUT_STYLE}
              />
            </div>
            {renderFields(draft, setDraftField)}
            {tabColumns && (
              <div>
                <label style={FIELD_LABEL_STYLE}>Columns</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={draftColumns === null} onChange={(e) => toggleUseDefaultColumns(e.target.checked)} />
                  Use my default columns
                </label>
                {draftColumns !== null && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, paddingLeft: 24 }}>
                    <SystemColumnRows systemColumns={tabColumns.systemColumns} rowStyle={DRAWER_COLUMN_ROW_STYLE} />
                    {tabColumns.columns.map((c, i) => (
                      <React.Fragment key={c.key}>
                        <ColumnGroupHeading columns={tabColumns.columns} index={i} style={{ marginTop: i === 0 ? 0 : 6 }} />
                        <label style={DRAWER_COLUMN_ROW_STYLE}>
                          <input type="checkbox" checked={draftColumns.includes(c.key)} onChange={() => toggleDraftColumn(c.key)} />
                          {c.label}
                        </label>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </FilterDrawer>
      )}
    </div>
  );
}

export default SavedFilterTabs;
